import { useEffect } from 'react';
import { haptic } from './telegram';

interface Options {
  onLeft?: () => void;
  onRight?: () => void;
  /** Выключить, когда жест мешает: открыто окно, идёт перетаскивание. */
  enabled?: boolean;
}

/**
 * Горизонтальные свайпы по экрану.
 *
 * В Telegram нет адресной строки и вкладок браузера, поэтому пролистывание
 * пальцем — единственный быстрый способ переключаться, не целясь в мелкие
 * кнопки нижней панели на ходу.
 *
 * Три условия, без которых жест мешает больше, чем помогает:
 *
 * 1. Движение должно быть заметно горизонтальнее вертикального — иначе
 *    обычная прокрутка ленты через раз перекидывает на соседнюю вкладку.
 * 2. Жест не считается, если начался на прокручиваемом по горизонтали
 *    элементе: таблицы отчётов и ряды чипов листаются вбок сами.
 * 3. Один палец. Двумя пальцами масштабируют карту, а не переключают вкладки.
 */
export function useSwipe({ onLeft, onRight, enabled = true }: Options) {
  useEffect(() => {
    if (!enabled) return;

    let x = 0;
    let y = 0;
    let live = false;

    const start = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        live = false;
        return;
      }

      // Внутри горизонтально прокручиваемого блока свайп — это прокрутка.
      const target = e.target as HTMLElement | null;
      /*
       * Лист «Меню» и модальные окна исключены отдельно.
       *
       * Внутри них свайп означает «пролистать список» или «закрыть окно»,
       * а не «сменить вкладку». Без этого движение пальцем по открытому
       * меню переключало разделы за спиной у человека.
       */
      if (
        target?.closest(
          '.sheet, .modal, .notes, .menu, .trackmap, .tbl-wrap, .chips, .seg, input, textarea, select',
        )
      ) {
        live = false;
        return;
      }

      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
      live = true;
    };

    const end = (e: TouchEvent) => {
      if (!live) return;
      live = false;

      const t = e.changedTouches[0];
      const dx = t.clientX - x;
      const dy = t.clientY - y;

      // Порог: 60 px и вдвое горизонтальнее, чем вертикально. Меньше —
      // и лента начинает перескакивать при обычной прокрутке.
      if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 2) return;

      const handler = dx < 0 ? onLeft : onRight;
      if (!handler) return;

      haptic('light');
      handler();
    };

    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchend', end, { passive: true });

    return () => {
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchend', end);
    };
  }, [onLeft, onRight, enabled]);
}
